import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { ServicePriceLimitHint } from "@/components/pricing/ServicePriceLimitHint";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { CountryMultiplierPreview } from "@/components/pricing/CountryMultiplierPreview";
import { FileText } from "@phosphor-icons/react";
import type { Service } from "@/types";
import { ServiceMembersField } from "@/components/session-members/ServiceMembersField";

export function ServiceFormDialog({
  service,
  isPending,
  onSubmit,
  onClose,
}: {
  service: Service | null;
  isPending: boolean;
  onSubmit: (data: Partial<Service>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(service?.name ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [duration, setDuration] = useState(String(service?.durationMinutes ?? "60"));
  const [price, setPrice] = useState<number | null>(
    // Sirova spremljena cijena: `basePrice` je akcijska kad postoji, `price` je sa
    // koeficijentom zemlje, a ovo polje prepisuje redovnu cijenu.
    service?.listPrice ?? null,
  );

  const [maxMembers, setMaxMembers] = useState<number | null>(service?.maxMembers ?? null);

  const numericPrice = price ?? 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      description,
      durationMinutes: Number(duration),
      price: numericPrice,
      maxMembers,
    });
  };

  return (
    <Dialog open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={FileText}
            title={service ? "Uredi uslugu" : "Nova usluga"}
            description={
              service
                ? "Izmijenite detalje postojeće usluge."
                : "Kreirajte novu uslugu dostupnu na platformi."
            }
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label htmlFor="svc-name">Naziv usluge</Label>
              <Input
                id="svc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="npr. Individualna terapija"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="svc-desc">Opis</Label>
              <Textarea
                id="svc-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Kratki opis usluge..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="svc-duration">Trajanje (min)</Label>
                <div className="relative">
                  <Input
                    id="svc-duration"
                    type="number"
                    min="1"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="pr-12"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    min
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="svc-price">Cijena</Label>
                <CurrencyInput
                  id="svc-price"
                  value={price}
                  onChange={setPrice}
                />
                {/* Admins may price outside the range, so this is informational only. */}
                <ServicePriceLimitHint price={null} />
              </div>
            </div>

            <ServiceMembersField idPrefix="svc" value={maxMembers} onChange={setMaxMembers} />

            <CountryMultiplierPreview clientPrice={numericPrice} />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending
              ? "Spremanje..."
              : service
                ? "Spremi promjene"
                : "Kreiraj uslugu"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
